import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";
import { useKeyedState } from "@/hooks/useKeyedState";

// The legacy key was force-written to `true` by any narrow-viewport visit,
// so its stored values don't reflect a real user choice. A fresh key starts
// everyone from the auto-open default; only explicit wide-screen collapses
// persist under it.
const LEGACY_COLLAPSED_KEY = "assistantSidebarCollapsed";
/** Exported so the loading skeleton can honor a persisted collapse. */
export const SESSION_SIDEBAR_COLLAPSED_KEY = "codesmithSidebarCollapsed";
type SetCollapsed = (value: boolean | ((prev: boolean) => boolean)) => void;

/**
 * Collapsed state for the session sidebar that only auto-opens when the
 * container has room for it (see `usePanelSpaceBudget`). While space exists
 * the stored preference is the source of truth; without space the sidebar
 * starts collapsed regardless, and a manual open is tracked separately so a
 * cramped window never rewrites the wide-screen preference.
 */
export function useSidebarCollapsedState(hasSpaceForSidebar) {
  const $ = _c(8);
  const [storedCollapsed, setStoredCollapsed] = useLocalStorage(SESSION_SIDEBAR_COLLAPSED_KEY, false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
  const [openedWhileNarrow, setOpenedWhileNarrow] = useKeyedState(hasSpaceForSidebar, false);
  const isSidebarCollapsed = hasSpaceForSidebar ? storedCollapsed : !openedWhileNarrow;
  let t1;
  if ($[1] !== hasSpaceForSidebar || $[2] !== setOpenedWhileNarrow || $[3] !== setStoredCollapsed) {
    t1 = value => {
      if (hasSpaceForSidebar) {
        setStoredCollapsed(value);
        return;
      }
      setOpenedWhileNarrow(prevOpened => {
        const nextCollapsed = typeof value === "function" ? value(!prevOpened) : value;
        return !nextCollapsed;
      });
    };
    $[1] = hasSpaceForSidebar;
    $[2] = setOpenedWhileNarrow;
    $[3] = setStoredCollapsed;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const setIsSidebarCollapsed = t1;
  let t2;
  if ($[5] !== isSidebarCollapsed || $[6] !== setIsSidebarCollapsed) {
    t2 = [isSidebarCollapsed, setIsSidebarCollapsed];
    $[5] = isSidebarCollapsed;
    $[6] = setIsSidebarCollapsed;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp() {
  window.localStorage.removeItem(LEGACY_COLLAPSED_KEY);
}